<template>
  <div class="business-details">
    <h1 class="page-title">{{ $t('customerBusinessDetails.title') }}</h1>

    <div class="details-container">
      <!-- Left Column: Business & Contact Information -->
      <div class="details-column">
        <div class="details-section">
          <div class="section-header">
            <h2 class="section-title">{{ $t('customerBusinessDetails.sections.businessInfo.title') }}</h2>
            <button 
              class="edit-btn" 
              @click="toggleEdit('business')"
              :class="{ 'active': editingSection === 'business' }"
            >
              {{ editingSection === 'business' ? $t('customerBusinessDetails.buttons.save') : $t('customerBusinessDetails.buttons.edit') }}
            </button>
          </div>
          <div class="details-grid">
            <div 
              class="detail-item" 
              v-for="(field, key) in businessFields" 
              :key="key"
              :class="{ 'editing': editingSection === 'business' }"
            >
              <label>{{ $t(`customerBusinessDetails.sections.businessInfo.fields.${key}`) }}</label>
              <div v-if="editingSection === 'business'" class="edit-input">
                <input 
                  v-if="field.type === 'date'"
                  type="date"
                  v-model="tempBusinessDetails[key]"
                  @blur="finishEditing(key, 'business')"
                  @keyup.enter="finishEditing(key, 'business')"
                >
                <input 
                  v-else
                  type="text"
                  v-model="tempBusinessDetails[key]"
                  @blur="finishEditing(key, 'business')"
                  @keyup.enter="finishEditing(key, 'business')"
                >
              </div>
              <p v-else>{{ formatValue(businessDetails[key], field.type) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import businessService from '@/services/businessService'

export default {
  name: 'CustomerBusinessDetails',
  props: {
    selectedBusiness: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      businessDetails: {
        // Business Info
        legalBusinessName: '',
        tradeName: '',
        entityType: '',
        state: '',
        zipCode: '',
        phoneNumber: '',
        emailAddress: '',
        businessAddress: '',
        businessCategory: '',
        
        // Dates
        startDate: '',
        declarationDate: '',
        
        // Package Info
        selectedPackage: '',
        packageStatus: '',
        packageStartDate: '',
        packageEndDate: '',
        
        // Responsible Party
        responsiblePartyName: '',
        role: '',
        businessActivity: '',
        reasonForApplying: '',
        
        // Additional Info
        businessExperience: '',
        employeeCount: '',
        businessProgress: '',
        businessGoals: '',
        
        // Status
        ein: '',
        status: ''
      },
      tempBusinessDetails: {},
      editingSection: null,
      editingField: null,
      businessFields: {
        legalBusinessName: { type: 'text' },
        tradeName: { type: 'text' },
        entityType: { type: 'text' },
        businessCategory: { type: 'text' },
        ein: { type: 'text' },
        state: { type: 'text' },
        startDate: { type: 'date' },
        declarationDate: { type: 'date' },
        businessAddress: { type: 'text' },
        zipCode: { type: 'text' },
        phoneNumber: { type: 'text' },
        emailAddress: { type: 'text' }
      }
    }
  },
  watch: {
    selectedBusiness: {
      immediate: true,
      handler(newBusiness) {
        if (newBusiness) {
          this.fetchBusinessDetails();
        }
      }
    }
  },
  methods: {
    async fetchBusinessDetails() {
      try {
        if (this.selectedBusiness && this.selectedBusiness.id) {
          const response = await businessService.getBusiness(this.selectedBusiness.id);
          if (response && response.data) {
            this.businessDetails = {
              ...this.businessDetails,
              ...response.data
            };
            this.tempBusinessDetails = { ...this.businessDetails };
          }
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return this.$t('customerBusinessDetails.notSpecified');
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale === 'en' ? 'en-US' : 'es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatValue(value, type) {
      if (!value) return this.$t('customerBusinessDetails.notSpecified');
      if (type === 'date') return this.formatDate(value);
      return value;
    },
    startEditing(field, section) {
      if (this.editingSection === section) {
        this.editingField = field;
      }
    },
    finishEditing(field, section) {
      this.editingField = null;
      if (section === 'business') {
        this.businessDetails[field] = this.tempBusinessDetails[field];
      }
    },
    async toggleEdit(section) {
      if (this.editingSection === section) {
        // Save changes
        try {
          await businessService.updateBusiness(this.selectedBusiness.id, this.businessDetails);
          this.editingSection = null;
          this.editingField = null;
        } catch (error) {
          console.error('Error updating business details:', error);
        }
      } else {
        // Start editing
        this.editingSection = section;
        this.tempBusinessDetails = { ...this.businessDetails };
      }
    }
  }
}
</script>

<style scoped>

.business-details {
  margin: 1rem 1.5rem;
  padding: 0 1rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.details-container {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.details-column {
  flex: 1;
  min-width: 300px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  padding-bottom: 0.25rem;
  border-bottom: 2px solid #e5e7eb;
  margin: 0;
}

.details-section {
  background-color: white;
  border-radius: 6px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background-color: #f9fafb;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.detail-item.editing {
  background-color: #f0fdf4;
  border: 1px solid #dcfce7;
}

.detail-item.editing label {
  color: #166534;
}

.detail-item label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
}

.detail-item p {
  font-size: 0.875rem;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
}

.edit-input input {
  width: 100%;
  padding: 0.375rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1f2937;
  background-color: white;
}

.edit-input input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.edit-btn {
  padding: 0.375rem 0.75rem;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.edit-btn:hover {
  background-color: #e5e7eb;
}

.edit-btn.active {
  background-color: #10b981;
  color: white;
  border-color: #059669;
}

.edit-btn::before {
  content: '';
  display: inline-block;
  width: 14px;
  height: 14px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.edit-btn:not(.active)::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'%3E%3C/path%3E%3C/svg%3E");
}

.edit-btn.active::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M5 13l4 4L19 7'%3E%3C/path%3E%3C/svg%3E");
}

@media (max-width: 768px) {
  .business-details {
    padding: 0.25rem;
  }

  .details-container {
    flex-direction: column;
    gap: 0.75rem;
  }

  .details-column {
    width: 100%;
  }

  .details-section {
    padding: 0.75rem;
  }

  .page-title {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
}
</style> 